import { EXERCISE_ORDER, type ExerciseKind } from '../domain/exercise';
import { addDays, weekStartOf } from './calendar';
import { type DaySession, doneKindsOn, earliestDay } from './sessions';

/** One row of the daily exercise log. */
export interface DayLog {
  /** ISO date (YYYY-MM-DD). */
  date: string;
  done: Record<ExerciseKind, boolean>;
  doneCount: number;
  /**
   * `in-progress` is today while any exercise is left; `complete` has all three done;
   * `partial` is a past day with some (or none) done.
   */
  status: 'in-progress' | 'complete' | 'partial';
}

/** One bar of "Sessions per week": exercises done on that day (0–3). */
export interface WeekBar {
  date: string;
  exercisesDone: number;
}

export const WEEK_DAYS = 7;
export const LOG_DAYS = 3;

/**
 * The Progress tab, derived from the person's sessions. "This week" is
 * the calendar week that holds today, Monday to Sunday — the chart's seven bars, with the
 * days still to come at zero — and every card uses that same week.
 */
export interface MyProgress {
  /** `logUntil` and the days before it, newest first; never before the first session. */
  log: DayLog[];
  /** The first day the log can show: the first session's, or today before there is one. */
  firstDate: string;
  /** Monday to Sunday of the current week, oldest first; days still to come read 0. */
  week: WeekBar[];
  /** Exercises done in those seven days — what the bars add up to. */
  exercisesThisWeek: number;
  /**
   * Days in those seven with all three exercises done — a session, the same unit as the
   * streaks and "this month" ("You completed N sessions this week", PRD-1987).
   */
  sessionsThisWeek: number;
  /** Share of those seven days on which each exercise was done, 0..1. */
  completionByKind: Record<ExerciseKind, number>;
  /** What to do next today, in routine order; undefined once today is complete. */
  nextKind: ExerciseKind | undefined;
}

/**
 * The daily log's rows for the day picked in its date selector (`until`, today when nothing
 * is picked) and the `logDays - 1` before it, newest first. `firstDate` bounds it like the
 * person's sessions do: the days before would only ever read "0 of 3 done".
 */
export function logRows(
  doneOn: (date: string) => Record<ExerciseKind, boolean>,
  today: string,
  until: string,
  firstDate: string | undefined,
  logDays = LOG_DAYS,
): DayLog[] {
  const log: DayLog[] = [];
  for (let back = 0; back < logDays; back += 1) {
    const date = addDays(until, -back);
    if (back > 0 && (firstDate === undefined || date < firstDate)) break;
    const done = doneOn(date);
    const doneCount = EXERCISE_ORDER.filter((kind) => done[kind]).length;
    const complete = doneCount === EXERCISE_ORDER.length;
    log.push({
      date,
      done,
      doneCount,
      status: complete ? 'complete' : date === today ? 'in-progress' : 'partial',
    });
  }
  return log;
}

export function deriveProgress(
  sessions: readonly DaySession[],
  today: string,
  logDays = LOG_DAYS,
  logUntil = today,
): MyProgress {
  const firstDate = earliestDay(sessions);
  const doneOn = (date: string) => {
    const kinds = doneKindsOn(sessions, date);
    return {
      reading: kinds.has('reading'),
      writing: kinds.has('writing'),
      math: kinds.has('math'),
    };
  };
  const log = logRows(doneOn, today, logUntil, firstDate, logDays);

  const week: WeekBar[] = [];
  const daysWithKind = { reading: 0, writing: 0, math: 0 };
  const weekStart = weekStartOf(today);
  for (let offset = 0; offset < WEEK_DAYS; offset += 1) {
    const date = addDays(weekStart, offset);
    const kinds = doneKindsOn(sessions, date);
    for (const kind of kinds) daysWithKind[kind] += 1;
    week.push({ date, exercisesDone: kinds.size });
  }

  const doneToday = doneKindsOn(sessions, today);
  return {
    log,
    firstDate: firstDate !== undefined && firstDate < today ? firstDate : today,
    week,
    exercisesThisWeek: week.reduce((sum, bar) => sum + bar.exercisesDone, 0),
    sessionsThisWeek: week.filter((bar) => bar.exercisesDone === EXERCISE_ORDER.length).length,
    completionByKind: {
      reading: daysWithKind.reading / WEEK_DAYS,
      writing: daysWithKind.writing / WEEK_DAYS,
      math: daysWithKind.math / WEEK_DAYS,
    },
    nextKind: EXERCISE_ORDER.find((kind) => !doneToday.has(kind)),
  };
}

export { addDays } from './calendar';

/** How far back the log's date selector lists days to jump to. */
export const LOG_PICKER_DAYS = 60;

/**
 * The days the log's date selector offers, newest first: from `last` (today) back to
 * `first`, at most `LOG_PICKER_DAYS` of them.
 */
export function logDayChoices(first: string, last: string, max = LOG_PICKER_DAYS): string[] {
  const days: string[] = [];
  for (let date = last; date >= first && days.length < max; date = addDays(date, -1)) {
    days.push(date);
  }
  return days;
}
